import { CustomElement, resolve } from 'aurelia';
import { ToyRelease, isReleaseUpcoming } from '../../models';
import { AccountService, SubscriptionService, UiService } from '../../services';
import template from './release-card.html';

// Renders a single release, either as a compact grid card or a detailed list row (driven by
// `view`). Image clicks open the shared lightbox and the notification bell toggles the release
// subscription — both via injected services rather than host callbacks. The bell's visual state
// (`canSubscribe`/`isSubscribed`/`subscriptionPending`) stays as bindables so the host can drive
// reactivity through the subscriptions signal.
export class ReleaseElement {
  private readonly ui: UiService = resolve(UiService);
  private readonly subscriptions: SubscriptionService = resolve(SubscriptionService);
  private readonly account: AccountService = resolve(AccountService);

  release: ToyRelease | null = null;
  view: 'list' | 'grid' = 'list';
  canSubscribe: boolean = false;
  isSubscribed: boolean = false;
  subscriptionPending: boolean = false;

  // True only for releases with a concrete availability instant still in the future. Gates the
  // timestamp so it appears together with the availability bell (which shares isReleaseUpcoming).
  get isUpcoming(): boolean {
    return !!this.release && isReleaseUpcoming(this.release);
  }

  // Tooltip for the bell. Signed-in users get the real toggle wording; anonymous visitors are
  // told that a free account unlocks the alert (matching the contextual nudge shown on click).
  get bellTitle(): string {
    if (!this.account.isAuthenticated) {
      return 'Create a free account to get an alert when this drops';
    }
    return this.isSubscribed
      ? 'Turn off availability alert for this release'
      : 'Email me when this release is available';
  }

  handleImageClick(): void {
    if (this.release?.imageUrl) {
      this.ui.openLightbox(this.release.imageUrl);
    }
  }

  handleBellClick(): void {
    if (!this.release) return;
    // Anonymous visitors can't subscribe; convert the intent into a sign-up nudge instead of
    // silently doing nothing.
    if (!this.account.isAuthenticated) {
      this.account.promptSignup('Create a free account and we\u2019ll email you the moment this release is available.');
      return;
    }
    this.subscriptions.toggleReleaseSubscription(this.release);
  }
}

export const Release = CustomElement.define({
  name: 'release',
  template,
  bindables: ['release', 'view', 'canSubscribe', 'isSubscribed', 'subscriptionPending']
}, ReleaseElement);
